import { Spin } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  IProtocolResponse,
  IProtocolTableContent,
} from "../../@types/Protocol";
import { CircleChecked } from "../../assets/icons/CircleChecked";
import { CircleUnchecked } from "../../assets/icons/CircleUncheked";
import { Dots } from "../../assets/icons/Dots";
import { CustomIconButton } from "../../components/CustomIconButton";
import { Dropdown, DropdownTextItem } from "../../components/Dropdown";
import { ActionContainer } from "../../components/TableActionDropdownButton/TableActionDropdownButton.styles";
import { useProtocols } from "../../hooks/queries/protocols";
import { useModal } from "../../hooks/useModal";
import { useToast } from "../../hooks/useToast";
import { PageLayout } from "../../layout/components/PageLayout";
import { PageTitle } from "../../layout/components/PageTitle";
import { protocolService } from "../../services/protocol.service";
import { ProtocolListTable } from "./components/ProtocolListTable";
import { WardPublishedProtocolModal } from "./components/WardPublishedProtocolModal/WardPublishedProtocolModal";
import {
  FullscreenSpin,
  ProtocolListContainer,
  ProtocolListTableContainer,
  PublishedCell,
} from "./ProtocolList.styles";

export function ProtocolList() {
  const { t } = useTranslation();
  const [isDropDownOpen, setIsDropDownOpen] = useState(false);
  const [dropdownResetKey, setDropdownResetKey] = useState(0);
  const [pendingProtocolId, setPendingProtocolId] = useState<string>();

  const { protocols, isFetching, isRefetching, refetch } = useProtocols();
  const { showToast } = useToast();
  const { openModal, closeModal } = useModal();

  const parsedProtocolsToTable = useMemo(() => {
    if (!protocols) return [];

    // EN-5191: ids, not labels. The handler below matched on the English menu text, so
    // translating the menu would have stopped publish, unpublish and delete working.
    const options = ["unpublish", "publish", "delete"];

    return protocols.map((protocol: IProtocolResponse) => {
      const isPending = pendingProtocolId === protocol.id;
      return {
        id: protocol.id,
        wardId: protocol.wardId,
        wardName: protocol.wardName,
        protocolName: protocol.protocolName,
        // EN-5283: an icon, as on Workflows, Care info and RAG, instead of the API's English
        // status word, which is an identity value ("Published") and so is not translated.
        published: (
          <PublishedCell>
            {protocol.status === "Published" ? (
              <CircleChecked />
            ) : (
              <CircleUnchecked />
            )}
          </PublishedCell>
        ),
        actionNames:
          protocol.actionNames.length > 36
            ? `${protocol.actionNames.substring(0, 36)}...`
            : protocol.actionNames,
        action: (
          <div>
            <ActionContainer
              className="actionContainer"
              style={{ justifyContent: "center" }}
              onClick={(e) => e.stopPropagation()}
            >
              {isPending ? (
                <Spin size="small" />
              ) : (
                <Dropdown
                  key={`${protocol.id}-${dropdownResetKey}`}
                  trigger={
                    <CustomIconButton
                      style={{ display: isDropDownOpen ? "none" : undefined }}
                    >
                      <Dots />
                    </CustomIconButton>
                  }
                  onOpenChange={(value) => setIsDropDownOpen(value)}
                  onValueChange={async (val: string) => {
                    if (pendingProtocolId) return;
                    setPendingProtocolId(protocol.id);

                    try {
                      if (val === options[0]) {
                        const result =
                          await protocolService.updateProtocolStatus(
                            protocol.id,
                            false
                          );

                        if (result.status >= 200 && result.status < 300) {
                          showToast(
                            t("protocols_toasts_unpublished"),
                            "success"
                          );
                          await refetch();
                        } else {
                          showToast(
                            t("protocols_toasts_unpublishFailed"),
                            "error"
                          );
                        }
                      }
                      if (val === options[1]) {
                        // Check if ward already has a published protocol before publishing
                        try {
                          const checkResult =
                            await protocolService.hasPublishedProtocol(
                              protocol.wardId.toString()
                            );

                          if (checkResult.data === true) {
                            openModal(
                              <WardPublishedProtocolModal
                                variant="list"
                                onClose={closeModal}
                              />,
                              {
                                width: "480px",
                                showCloseButton: false,
                              }
                            );
                            return;
                          }
                        } catch {
                          // If check fails, proceed with publish and let backend handle it
                        }

                        const result =
                          await protocolService.updateProtocolStatus(
                            protocol.id,
                            true
                          );

                        if (result.status >= 200 && result.status < 300) {
                          showToast(t("protocols_toasts_published"), "success");
                          await refetch();
                        } else {
                          showToast(
                            t("protocols_toasts_publishFailed"),
                            "error"
                          );
                        }
                      }
                      if (val === options[2]) {
                        const result = await protocolService.archiveProtocol(
                          protocol.id
                        );

                        if (result.status >= 200 && result.status < 300) {
                          showToast(t("protocols_toasts_deleted"), "success");
                          await refetch();
                        } else {
                          showToast(
                            t("protocols_toasts_deleteFailed"),
                            "error"
                          );
                        }
                      }
                    } finally {
                      setPendingProtocolId(undefined);
                      setDropdownResetKey((prev) => prev + 1);
                    }
                  }}
                >
                  {protocol.status === "Published" ? (
                    <DropdownTextItem
                      key="unpublish"
                      value="unpublish"
                      text={t("protocols_actions_unpublish")}
                    />
                  ) : (
                    <DropdownTextItem
                      key="publish"
                      value="publish"
                      text={t("protocols_actions_publish")}
                    />
                  )}
                  <DropdownTextItem
                    key="delete"
                    value="delete"
                    text={t("protocols_actions_delete")}
                  />
                </Dropdown>
              )}
            </ActionContainer>
          </div>
        ),
      } as IProtocolTableContent;
    });
  }, [protocols, dropdownResetKey, pendingProtocolId, isDropDownOpen, t]);

  return (
    <PageLayout>
      <ProtocolListContainer>
        <PageTitle>{t("protocols_title")}</PageTitle>

        <ProtocolListTableContainer>
          <ProtocolListTable
            content={parsedProtocolsToTable}
            isLoading={isFetching || isRefetching}
            refetch={refetch}
          />
        </ProtocolListTableContainer>
      </ProtocolListContainer>
      {pendingProtocolId && <FullscreenSpin fullscreen size="large" />}
    </PageLayout>
  );
}
